import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Chip
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { alpha } from "@mui/material/styles";

const RecycleBinCompanyCard = ({ company, onRestore, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const calculateExpirationDate = (deletedAt) => {
    if (!deletedAt) return "Unknown";
    const deletedDate = new Date(deletedAt);
    if (isNaN(deletedDate)) return "Unknown";
    const expirationDate = new Date(
      deletedDate.getTime() + 30 * 24 * 60 * 60 * 1000
    );
    return expirationDate.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRestore = () => {
    onRestore(company);
    handleMenuClose();
  };

  const handleDelete = () => {
    onDelete(company);
    handleMenuClose();
  };

  return (
    <Card
      sx={{
        height: 240,
        borderRadius: 2,
        background: "white",
        border: "1px solid",
        borderColor: "divider",
        transition: "all 0.2s ease",
        position: "relative",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: (theme) =>
            `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
          borderColor: "primary.main",
          "& .company-options": {
            opacity: 1,
            transform: "translateY(0)"
          }
        }
      }}
    >
      <CardContent sx={{ height: "100%", p: 3 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            position: "relative"
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 1.5,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                transition: "all 0.3s ease-in-out"
              }}
            >
              <BusinessIcon color='primary' fontSize='small' />
            </Box>
          </Box>

          <IconButton
            className='company-options'
            sx={{
              position: "absolute",
              top: -8,
              right: -8,
              backgroundColor: "white",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              opacity: 0,
              transform: "translateY(10px)",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: "grey.100",
                transform: "translateY(0) scale(1.1)"
              }
            }}
            onClick={handleMenuClick}
          >
            <MoreVertIcon />
          </IconButton>

          <Typography
            variant='h6'
            fontWeight={600}
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {company.name}
          </Typography>

          <Chip
            label={company.uen}
            size='small'
            variant='outlined'
            sx={{
              alignSelf: "flex-start",
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08)
            }}
          />

          <Box
            sx={{
              mt: "auto",
              pt: 2,
              borderTop: "1px solid",
              borderColor: "divider"
            }}
          >
            <Typography
              variant='caption'
              sx={{
                color: "text.secondary",
                display: "flex",
                alignItems: "center",
                gap: 1
              }}
            >
              <CalendarTodayIcon fontSize='small' />
              YA {company.yearOfAssessment ?? "—"}
              <Box component='span' sx={{ mx: 1, color: "divider" }}>
                |
              </Box>
              {company.financialYearEnd
                ? new Date(company.financialYearEnd).toLocaleString("default", {
                    year: "numeric",
                    month: "short"
                  })
                : "N/A"}
            </Typography>
            <Typography
              variant='caption'
              sx={{
                color: "error.main",
                fontWeight: 500,
                display: "block",
                mt: 0.5
              }}
            >
              Expires: {calculateExpirationDate(company.deleted_at)}
            </Typography>
          </Box>
        </Box>
      </CardContent>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            boxShadow: "0 8px 32px 0 rgba(96,63,151,0.18)",
            borderRadius: 3,
            border: (theme) => `0.75px solid ${theme.palette.primary.light}`,
            minWidth: "210px",
            mt: 1.5,
            p: 0.5
          }
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={handleRestore}
          sx={{
            py: 1.5,
            px: 2.5,
            fontWeight: 500,
            fontSize: "1rem",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            borderRadius: 1.5,
            transition: "background 0.2s",
            color: "success.main",
            "&:hover": {
              bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
              color: "success.main",
              svg: { color: "success.main" }
            }
          }}
        >
          <RestoreFromTrashIcon
            fontSize='small'
            sx={{ color: "success.main", mr: 1 }}
          />
          Restore Company
        </MenuItem>
        <MenuItem
          onClick={handleDelete}
          sx={{
            py: 1.5,
            px: 2.5,
            fontWeight: 500,
            fontSize: "1rem",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            borderRadius: 1.5,
            color: "error.main",
            transition: "background 0.2s",
            svg: { color: "error.main" },
            "&:hover": {
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
              color: "error.dark",
              svg: { color: "error.dark" }
            }
          }}
        >
          <DeleteIcon fontSize='small' sx={{ mr: 1 }} />
          Delete Permanently
        </MenuItem>
      </Menu>
    </Card>
  );
};

export default RecycleBinCompanyCard;
